import React, { Suspense, lazy, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const CogScene = lazy(() => import('./CogScene'));

interface CogCardProps {
    width?: number;
    height?: number;
    depth?: number;
    className?: string;
    theme?: 'light' | 'dark';
    cog?: {
        width: number;
        length: number;
        height: number;
    };
    weight?: number;
}

const CogCard: React.FC<CogCardProps> = ({
    width,
    height,
    depth,
    className = "",
    theme = 'dark',
    cog,
    weight = 0,
}) => {
    const { t } = useLanguage();
    // Use provided dimensions or fallback defaults only when undefined/null
    const dimensions = {
        width: width ?? 3,
        height: height ?? 1.5,
        depth: depth ?? 2
    };
    const [zoom, setZoom] = useState(1);

    // Normalize dimensions for 3D display
    // Visual limits: min 0.5m, max: L=8m, W=4m, H=4m
    const normalize3DDimensions = (dims: { width: number; height: number; depth: number }) => {
        const MIN_SIZE = 0.5;
        const MAX_LENGTH = 8;  // Boy (L)
        const MAX_WIDTH = 4;   // En (W)
        const MAX_HEIGHT = 4;  // Yükseklik (H)

        // Clamp very small values up to a minimum visual size
        const clampedWidth = Math.max(dims.width, MIN_SIZE);
        const clampedDepth = Math.max(dims.depth, MIN_SIZE);
        const clampedHeight = Math.max(dims.height, MIN_SIZE);

        // Calculate scale factors for each dimension
        const scaleLength = MAX_LENGTH / clampedWidth;
        const scaleWidth = MAX_WIDTH / clampedDepth;
        const scaleHeight = MAX_HEIGHT / clampedHeight;

        // Use the smallest scale to maintain proportions
        const SCENE_PADDING_FACTOR = 0.82; // Max zoom for detailed view
        const scale = Math.min(scaleLength, scaleWidth, scaleHeight) * SCENE_PADDING_FACTOR;

        return {
            width: clampedWidth * scale,
            height: clampedHeight * scale,
            depth: clampedDepth * scale,
            scale
        };
    };

    const normalized = normalize3DDimensions(dimensions);

    const normalizedCog = cog
        ? {
            width: cog.width * normalized.scale,
            length: cog.length * normalized.scale,
            height: cog.height * normalized.scale,
        }
        : undefined;



    const isLight = theme === 'light';

    const sceneBgClass = isLight ? 'bg-slate-50' : 'bg-slate-800';



    const valueTextClasses = isLight ? 'font-mono text-slate-900' : 'font-mono text-slate-50';

    return (
        <div className={`rounded-xl overflow-hidden flex flex-col ${isLight ? 'bg-white border border-slate-200' : 'bg-slate-800 border border-slate-700'} ${className}`}>
            {/* Header removed as per user request */}

            {/* 3D Scene Container - now using flex-1 to fill all available space */}
            <div className={`relative w-full flex-1 ${sceneBgClass}`}>
                <Suspense fallback={
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-sm text-gray-400">3D Sahne Yükleniyor...</p>
                        </div>
                    </div>
                }>
                    <CogScene dimensions={normalized} cog={normalizedCog} theme={theme} zoom={zoom} />
                </Suspense>

                {/* Overlay Cargo Info (Top Left) */}
                <div className="absolute top-3 left-3 z-10 pointer-events-none">
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 border-b pb-1.5 mb-0.5 border-dashed border-opacity-20 border-current"
                            style={{ borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}>
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                            <span className={`text-[10px] font-bold tracking-wider uppercase ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                                {t('labels.cargo_specs') || 'CARGO'}
                            </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            {/* Row 1: Weight */}
                            <div className="flex items-baseline gap-2">
                                <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500 uppercase tracking-tighter'}`}>Weight:</span>
                                <span className={`text-[12px] font-mono font-bold ${valueTextClasses}`}>{weight.toLocaleString()} MT</span>
                            </div>

                            {/* Row 2: Dimensions */}
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>L:</span>
                                    <span className={`text-[11px] font-mono ${valueTextClasses}`}>{width?.toFixed(2)}m</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>W:</span>
                                    <span className={`text-[11px] font-mono ${valueTextClasses}`}>{depth?.toFixed(2)}m</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>H:</span>
                                    <span className={`text-[11px] font-mono ${valueTextClasses}`}>{height?.toFixed(2)}m</span>
                                </div>
                            </div>

                            {/* Row 3: CoG */}
                            {cog && (
                                <div className="flex items-center gap-3 pt-0.5 mt-0.5 border-t border-white/5 border-dashed">
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[9px] font-bold text-amber-500/90`}>CoG (L):</span>
                                        <span className={`text-[11px] font-mono ${valueTextClasses}`}>{cog.length.toFixed(2)}m</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[9px] font-bold text-sky-500/90`}>CoG (W):</span>
                                        <span className={`text-[11px] font-mono ${valueTextClasses}`}>{cog.width.toFixed(2)}m</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[9px] font-bold text-emerald-500/90`}>CoG (H):</span>
                                        <span className={`text-[11px] font-mono ${valueTextClasses}`}>{cog.height.toFixed(2)}m</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Zoom Controls - Top Right */}
                <div className="absolute top-3 right-3 pointer-events-auto z-20">
                    <div className={`flex items-center gap-1 p-1.5 rounded-md border backdrop-blur-sm ${isLight
                        ? 'bg-white/90 border-slate-200 shadow-lg'
                        : 'bg-black/80 border-white/20 shadow-xl'}`}>
                        <button
                            onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1))}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-all duration-200 ${isLight
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                                : 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                                }`}
                            title="Zoom Out"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                        </button>

                        <div className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isLight
                            ? 'bg-slate-100 text-slate-700 border border-slate-300'
                            : 'bg-slate-700 text-white border border-slate-600'
                            }`}>
                            {Math.round(zoom * 100)}%
                        </div>

                        <button
                            onClick={() => setZoom(prev => Math.min(3, prev + 0.1))}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-all duration-200 ${isLight
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                                : 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                                }`}
                            title="Zoom In"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    </div>
                </div>

            </div>

        </div>
    );
};

export default CogCard;
